<div class="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 overflow-y-auto">
	<!-- Header with Search -->
	<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
		<div>
			<h1 class="text-2xl font-bold text-[#5d202d]">Customers</h1>
			<p class="text-sm text-gray-500 mt-1">Manage your customer database</p>
		</div>
		<div class="relative">
			<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
			</svg>
			<input type="text" placeholder="Search customers..." [value]="searchQuery()" (input)="onSearch($event)"
				class="pl-10 pr-4 py-2.5 w-full sm:w-80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] bg-white shadow-sm transition-all" />
		</div>
	</div>

	<!-- Loading State -->
	@if (isLoading()) {
	<div class="flex justify-center py-16">
		<div class="animate-spin rounded-full h-10 w-10 border-3 border-[#5d202d] border-t-transparent"></div>
	</div>
	} @else {
	<!-- Customers Grid -->
	<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
		@for (customer of customers(); track customer.id) {
		<div class="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-[#caa78a]/50 transition-all duration-200 overflow-hidden group">
			<div class="p-5">
				<div class="flex items-start justify-between">
					<div class="flex items-center gap-3">
						<div class="w-12 h-12 rounded-full bg-gradient-to-br from-[#5d202d] to-[#8c6a4f] flex items-center justify-center text-white font-semibold text-lg shadow-sm">
							{{ customer.first_name.charAt(0) }}{{ customer.last_name.charAt(0) }}
						</div>
						<div>
							<h3 class="font-semibold text-gray-900">{{ customer.first_name }} {{ customer.last_name }}</h3>
							@if (customer.has_had_consultation) {
							<span class="inline-flex items-center gap-1 text-xs text-green-600">
								<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
								</svg>
								Consulted
							</span>
							} @else {
							<span class="inline-flex items-center gap-1 text-xs text-[#caa78a]">
								<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
								</svg>
								New Customer
							</span>
							}
						</div>
					</div>
				</div>

				<div class="mt-4 space-y-2">
					<div class="flex items-center gap-2 text-sm text-gray-600">
						<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
						</svg>
						<span class="truncate">{{ customer.email || 'No email' }}</span>
					</div>
					<div class="flex items-center gap-2 text-sm text-gray-600">
						<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
						</svg>
						<span>{{ customer.phone }}</span>
					</div>
				</div>
			</div>

			<!-- Card Actions -->
			<div class="px-5 py-3 bg-gray-50/50 border-t border-gray-100 flex justify-end gap-2">
				<button (click)="viewDetails(customer)" class="px-3 py-1.5 text-sm font-medium text-[#5d202d] hover:bg-[#5d202d]/10 rounded-lg transition-colors">
					View Details
				</button>
				<button (click)="openModal(customer)" class="px-3 py-1.5 text-sm font-medium text-white bg-[#5d202d] hover:bg-[#5d202d]/90 rounded-lg transition-colors">
					Edit
				</button>
			</div>
		</div>
		} @empty {
		<div class="col-span-full">
			<div class="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
				<svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
				</svg>
				<h3 class="text-lg font-semibold text-gray-900 mb-1">No customers found</h3>
				<p class="text-gray-500">Start by adding your first customer</p>
			</div>
		</div>
		}
	</div>

	<!-- Pagination -->
	@if (totalPages() > 1) {
	<div class="flex items-center justify-center gap-2 mt-6">
		<button (click)="changePage(currentPage() - 1)" [disabled]="currentPage() === 1"
			class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
			</svg>
		</button>
		<span class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg">
			Page {{ currentPage() }} of {{ totalPages() }}
		</span>
		<button (click)="changePage(currentPage() + 1)" [disabled]="currentPage() === totalPages()"
			class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
			</svg>
		</button>
	</div>
	}
	}
</div>

<!-- Edit/Add Modal -->
@if (showModal()) {
<div class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
	<div class="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
		<div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
			<h3 class="text-xl font-semibold text-[#5d202d]">{{ isEditMode() ? 'Edit Customer' : 'Add Customer' }}</h3>
			<button (click)="closeModal()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
				<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
				</svg>
			</button>
		</div>

		<form [formGroup]="customerForm" (ngSubmit)="saveCustomer()" class="p-6 space-y-4">
			@if (errorMessage()) {
			<div class="p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
				<svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
				</svg>
				{{ errorMessage() }}
			</div>
			}

			<div class="grid grid-cols-2 gap-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 mb-1.5">First Name *</label>
					<input type="text" formControlName="first_name"
						class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all" />
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 mb-1.5">Last Name *</label>
					<input type="text" formControlName="last_name"
						class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all" />
				</div>
			</div>

			<div>
				<label class="block text-sm font-medium text-gray-700 mb-1.5">Phone *</label>
				<input type="text" formControlName="phone"
					class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all" />
			</div>

			<div>
				<label class="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
				<input type="email" formControlName="email"
					class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all" />
			</div>

			<div>
				<label class="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
				<textarea formControlName="notes" rows="3"
					class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#5d202d]/20 focus:border-[#5d202d] transition-all resize-none"></textarea>
			</div>

			<div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
				<button type="button" (click)="closeModal()"
					class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
					Cancel
				</button>
				<button type="submit" [disabled]="isSubmitting() || customerForm.invalid"
					class="px-5 py-2.5 text-sm font-medium text-white bg-[#5d202d] hover:bg-[#5d202d]/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
					{{ isSubmitting() ? 'Saving...' : 'Save Customer' }}
				</button>
			</div>
		</form>
	</div>
</div>
}

<!-- Customer Details Modal Template -->
<ng-template #detailsModalTemplate>
<div class="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
	<!-- Modal Header -->
	<div class="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#5d202d] to-[#8c6a4f]">
		<h3 class="text-xl font-semibold text-white">Customer Details</h3>
		<button (click)="closeDetailsModal()" class="p-2 hover:bg-white/20 rounded-lg transition-colors">
			<svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
			</svg>
		</button>
	</div>

	@if (isLoadingDetails()) {
	<div class="flex justify-center py-16">
		<div class="animate-spin rounded-full h-10 w-10 border-3 border-[#5d202d] border-t-transparent"></div>
	</div>
	} @else if (selectedCustomer()) {
	<div class="p-6 overflow-y-auto">
		<!-- Customer Profile Card -->
		<div class="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
			<div class="w-16 h-16 rounded-full bg-gradient-to-br from-[#5d202d] to-[#8c6a4f] flex items-center justify-center text-white font-bold text-xl shadow-lg">
				{{ selectedCustomer()!.first_name.charAt(0) }}{{ selectedCustomer()!.last_name.charAt(0) }}
			</div>
			<div>
				<h4 class="text-xl font-semibold text-gray-900">{{ selectedCustomer()!.first_name }} {{ selectedCustomer()!.last_name }}</h4>
				<div class="flex items-center gap-2 mt-1">
					@if (selectedCustomer()!.has_had_consultation) {
					<span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
						<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
						</svg>
						Had Consultation
					</span>
					} @else {
					<span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-[#caa78a]/20 text-[#8c6a4f]">
						<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
						</svg>
						No Consultation Yet
					</span>
					}
				</div>
			</div>
		</div>

		<!-- Contact Info -->
		<div class="grid grid-cols-2 gap-4 mb-6">
			<div class="bg-gray-50 rounded-xl p-4">
				<div class="flex items-center gap-2 text-gray-500 text-sm mb-1">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
					</svg>
					Phone
				</div>
				<p class="font-medium text-gray-900">{{ selectedCustomer()!.phone }}</p>
			</div>
			<div class="bg-gray-50 rounded-xl p-4">
				<div class="flex items-center gap-2 text-gray-500 text-sm mb-1">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
					</svg>
					Email
				</div>
				<p class="font-medium text-gray-900">{{ selectedCustomer()!.email || '-' }}</p>
			</div>
		</div>

		@if (selectedCustomer()!.notes) {
		<div class="bg-[#caa78a]/10 rounded-xl p-4 mb-6">
			<div class="flex items-center gap-2 text-[#8c6a4f] text-sm mb-1">
				<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
				</svg>
				Notes
			</div>
			<p class="text-gray-700">{{ selectedCustomer()!.notes }}</p>
		</div>
		}

		<!-- Appointment History -->
		<div>
			<h4 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
				<svg class="w-5 h-5 text-[#5d202d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
				</svg>
				Appointment History
				<span class="text-sm font-normal text-gray-500">({{ selectedCustomer()!.appointments?.length || 0 }})</span>
			</h4>

			@if (selectedCustomer()!.appointments && selectedCustomer()!.appointments!.length > 0) {
			<div class="space-y-3 max-h-[250px] overflow-y-auto pr-1">
				@for (apt of selectedCustomer()!.appointments; track apt.id) {
				<div class="border border-gray-100 rounded-xl p-4 hover:border-[#caa78a]/50 hover:shadow-sm transition-all">
					<div class="flex justify-between items-start">
						<div>
							<p class="font-medium text-gray-900">
								@if (apt.is_consultation) {
								<span class="text-[#5d202d]">Free Consultation</span>
								} @else {
								{{ apt.service?.service_name || 'Service' }}
								}
							</p>
							<div class="flex items-center gap-3 mt-1 text-sm text-gray-500">
								<span class="flex items-center gap-1">
									<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
									</svg>
									{{ apt.appointment_date | date:'mediumDate' }}
								</span>
								<span class="flex items-center gap-1">
									<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
									</svg>
									{{ apt.start_time }}
								</span>
							</div>
							@if (apt.staff) {
							<p class="text-xs text-gray-400 mt-1">with {{ apt.staff.first_name }} {{ apt.staff.last_name }}</p>
							}
						</div>
						<div class="text-right">
							<span [class]="'px-2.5 py-1 text-xs font-medium rounded-full ' + getStatusColor(apt.status)">
								{{ apt.status | titlecase }}
							</span>
							@if (apt.total_amount) {
							<p class="text-sm font-semibold text-[#5d202d] mt-2">₹{{ apt.total_amount | number }}</p>
							}
						</div>
					</div>
				</div>
				}
			</div>
			} @else {
			<div class="text-center py-8 bg-gray-50 rounded-xl">
				<svg class="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
				</svg>
				<p class="text-gray-500 text-sm">No appointments yet</p>
			</div>
			}
		</div>
	</div>
	}

	<div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
		<button (click)="closeDetailsModal()"
			class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors">
			Close
		</button>
	</div>
</div>
</ng-template>
